import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import NotificationCenter from '../../components/NotificationCenter';
import {
  CreditCard,
  Calendar,
  Gift,
  MessageSquare,
  QrCode,
  ArrowRight,
  Clock,
  CheckCircle2,
  AlertCircle,
  BookOpen,
  Loader2,
  Download,
  Bell,
  Video,
  Award,
  Star,
  Trophy
} from 'lucide-react';

const DashboardHomePage = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [webinarRegistrations, setWebinarRegistrations] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [almostUnlockedBadges, setAlmostUnlockedBadges] = useState([]);
  const [earnedBadges, setEarnedBadges] = useState([]);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const [summaryRes, webinarsRes, almostRes, badgesRes] = await Promise.all([
          api.get('/dashboard/summary'),
          api.get('/dashboard/webinar-registrations').catch(() => ({ data: [] })),
          api.get('/badges/almost-unlocked').catch(() => ({ data: { almost_unlocked: [] } })),
          api.get('/badges/my-badges').catch(() => ({ data: { badges: [] } }))
        ]);
        setDashboardData(summaryRes.data);
        setWebinarRegistrations(webinarsRes.data);
        setAlmostUnlockedBadges(almostRes.data.almost_unlocked || []);
        setEarnedBadges(badgesRes.data.badges || []);
      } catch (error) {
        console.error('Error fetching dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const membershipProgress = dashboardData?.membership?.days_remaining 
    ? Math.max(0, Math.min(100, ((365 - dashboardData.membership.days_remaining) / 365) * 100)) 
    : 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-2">
                ¡Hola, {dashboardData?.user?.name?.split(' ')[0] || user?.name?.split(' ')[0]}!
              </h1>
              <p className="text-gray-300">
                Bienvenido a tu portal de afiliado UPIM
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <Button className="bg-white text-gray-900 hover:bg-gray-100" asChild>
                <Link to="/dashboard/credencial">
                  <QrCode className="mr-2 h-5 w-5" />
                  Ver mi credencial
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Estado de Membresía</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {dashboardData?.membership?.status === 'active' ? 'Activa' : 'Inactiva'}
                  </p>
                </div>
                <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                  dashboardData?.membership?.status === 'active' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {dashboardData?.membership?.status === 'active' ? (
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  ) : (
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  )}
                </div>
              </div>
              {dashboardData?.membership && (
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500">Vigencia</span>
                    <span className="text-gray-700">{dashboardData.membership.days_remaining || 0} días restantes</span>
                  </div>
                  <Progress value={membershipProgress} className="h-2" />
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pagos Realizados</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {dashboardData?.total_payments || 0}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">en tu historial</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Eventos Registrados</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {dashboardData?.upcoming_events || 0}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">próximos</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Beneficios</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    20+
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Gift className="h-6 w-6 text-amber-600" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">disponibles</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Credential Preview */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Mi Credencial Digital</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/dashboard/credencial">
                  Ver completa
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-6 p-4 bg-gradient-to-r from-gray-100 to-gray-50 rounded-xl">
                {dashboardData?.credential_qr && (
                  <div className="flex-shrink-0">
                    <img 
                      src={`data:image/png;base64,${dashboardData.credential_qr}`}
                      alt="QR Credencial"
                      className="w-24 h-24 rounded-lg"
                    />
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-lg text-gray-900">{dashboardData?.user?.name}</h3>
                  <p className="text-gray-600">{dashboardData?.user?.company || 'Asesor Independiente'}</p>
                  <p className="text-sm text-gray-500 mt-1">{dashboardData?.user?.email}</p>
                  <Badge className="mt-2 bg-gray-800 text-white">
                    {dashboardData?.membership?.plan || 'Sin membresía'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Acciones Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to="/dashboard/credencial">
                  <QrCode className="mr-3 h-5 w-5 text-gray-600" />
                  Ver Credencial QR
                </Link>
              </Button>
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to="/afiliacion">
                  <CreditCard className="mr-3 h-5 w-5 text-blue-600" />
                  Renovar Membresía
                </Link>
              </Button>
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to="/calendario">
                  <Calendar className="mr-3 h-5 w-5 text-purple-600" />
                  Ver Eventos
                </Link>
              </Button>
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to="/academia">
                  <BookOpen className="mr-3 h-5 w-5 text-amber-600" />
                  Academia UPIM
                </Link>
              </Button>
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to="/mis-constancias">
                  <Award className="mr-3 h-5 w-5 text-indigo-600" />
                  Mis Constancias
                </Link>
              </Button>
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to="/mis-insignias">
                  <Trophy className="mr-3 h-5 w-5 text-amber-500" />
                  Mis Insignias
                </Link>
              </Button>
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to="/webinars">
                  <Video className="mr-3 h-5 w-5 text-red-600" />
                  Ver Webinars
                </Link>
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell className="mr-3 h-5 w-5 text-green-600" />
                Notificaciones
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Notification Center */}
        {showNotifications && (
          <NotificationCenter 
            webinars={webinarRegistrations.map(r => r.webinar).filter(Boolean)}
            onClose={() => setShowNotifications(false)}
          />
        )}

        {/* Almost Unlocked Badges */}
        {almostUnlockedBadges.length > 0 && (
          <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center text-amber-700">
                <Star className="h-5 w-5 mr-2 text-amber-500" />
                ¡Casi desbloqueas nuevas insignias!
              </CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/mis-insignias">
                  Ver todas
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {almostUnlockedBadges.slice(0, 3).map(badge => (
                  <div 
                    key={badge.badge_id}
                    className="bg-white rounded-xl p-4 border border-amber-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white"
                        style={{ backgroundColor: badge.color || '#f59e0b' }}
                      >
                        <Star className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{badge.name}</h4>
                        <p className="text-sm text-amber-600">+{badge.points} puntos</p>
                      </div>
                    </div>
                    <Progress value={badge.progress_percent} className="h-2 mb-2" />
                    <p className="text-xs text-gray-600">{badge.remaining_text}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Earned Badges Preview */}
        {earnedBadges.length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center">
                <Trophy className="h-5 w-5 mr-2 text-amber-500" />
                Mis Insignias ({earnedBadges.length})
              </CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/mis-insignias">
                  Ver todas
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {earnedBadges.slice(0, 6).map(badge => (
                  <div 
                    key={badge.id}
                    className="flex items-center gap-2 px-3 py-2 rounded-full text-white text-sm font-medium"
                    style={{ backgroundColor: badge.color || '#6366f1' }}
                  >
                    <Star className="w-4 h-4" />
                    {badge.name}
                  </div>
                ))}
                {earnedBadges.length > 6 && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-gray-200 text-gray-700 text-sm">
                    +{earnedBadges.length - 6} más
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Registered Webinars */}
        {webinarRegistrations.length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center">
                <Video className="h-5 w-5 mr-2 text-gray-600" />
                Mis Webinars
              </CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/webinars">
                  Ver todos
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {webinarRegistrations.slice(0, 3).map(reg => (
                  <div 
                    key={reg.registration_id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">
                        {reg.webinar?.title}
                      </h4>
                      <div className="flex items-center mt-1 text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-1" />
                        {reg.webinar?.date ? new Date(reg.webinar.date).toLocaleDateString('es-MX', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'Fecha por confirmar'}
                      </div>
                    </div>
                    <Button size="sm" className="bg-gray-900 hover:bg-gray-800 ml-4" asChild>
                      <Link to={`/webinars/${reg.webinar_id}/sala`}>
                        Entrar
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Registered Events */}
        {dashboardData?.registered_events?.length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Mis Eventos Registrados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {dashboardData.registered_events.map((event) => (
                  <div key={event.id} className="flex space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900">{event.title}</h4>
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        {event.date ? new Date(event.date).toLocaleDateString('es-MX', { 
                          day: 'numeric', 
                          month: 'short',
                          year: 'numeric'
                        }) : 'Fecha por confirmar'}
                      </div>
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        <Clock className="h-4 w-4 mr-1" />
                        {event.location || 'Ubicación por confirmar'}
                      </div>
                      <Badge className={`mt-2 ${event.status === 'checked_in' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {event.status === 'checked_in' ? 'Asistió' : 'Registrado'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment History */}
        {dashboardData?.payment_history?.length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Historial de Pagos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboardData.payment_history.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{payment.concept}</p>
                      <p className="text-sm text-gray-500">
                        {payment.date ? new Date(payment.date).toLocaleDateString('es-MX') : ''}
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <p className="font-bold text-gray-900">${payment.amount?.toLocaleString()} MXN</p>
                        <Badge className={payment.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                          {payment.status === 'completed' ? 'Completado' : 'Pendiente'}
                        </Badge>
                      </div>
                      {payment.status === 'completed' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${payment.id}`, '_blank')}
                          title="Descargar recibo"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardHomePage;
